import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  applyAppearance,
  DEFAULT_APPEARANCE,
  type AppearanceSettings,
  type FontFamilyKey,
  type PageBackgroundKey,
  type SidebarType,
} from '../lib/appearance';

interface AppearanceState {
  settings: AppearanceSettings;
  applySettings: (settings: AppearanceSettings) => void;
  setAccentHex: (hex: string | null) => void;
  setFontFamily: (fontFamily: FontFamilyKey) => void;
  setPageBackground: (pageBackground: PageBackgroundKey) => void;
  setSidebarType: (sidebarType: SidebarType) => void;
  resetSettings: () => void;
}

export const useAppearanceStore = create<AppearanceState>()(
  persist(
    (set) => ({
      settings: DEFAULT_APPEARANCE,
      applySettings: (settings) => set({ settings }),
      setAccentHex: (accentHex) =>
        set((state) => ({ settings: { ...state.settings, accentHex } })),
      setFontFamily: (fontFamily) =>
        set((state) => ({ settings: { ...state.settings, fontFamily } })),
      setPageBackground: (pageBackground) =>
        set((state) => ({ settings: { ...state.settings, pageBackground } })),
      setSidebarType: (sidebarType) =>
        set((state) => ({ settings: { ...state.settings, sidebarType } })),
      resetSettings: () => set({ settings: DEFAULT_APPEARANCE }),
    }),
    {
      name: 'moneyeu-appearance',
      storage: createJSONStorage(() => localStorage),
      merge: (persisted, current) => ({
        ...current,
        settings: {
          ...DEFAULT_APPEARANCE,
          ...(persisted as { settings?: Partial<AppearanceSettings> } | undefined)?.settings,
        },
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.settings) {
          const theme =
            (localStorage.getItem('moneyeu-theme') as 'light' | 'dark') || 'dark';
          applyAppearance(state.settings, theme);
        }
      },
    }
  )
);
